import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import './PrintPrescription.css';
import logo from './clinic.png';

const PrintPrescription = ({ visit, patient, doctorData, autoPrint, onAfterPrint }) => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const [doctorInfo, setDoctorInfo] = useState(null);
    const lastPrintedVisitId = useRef(null);

    useEffect(() => {
        if (doctorData) {
            setDoctorInfo(doctorData);
            return;
        }

        const storedDoctorData = localStorage.getItem('doctorProfile');
        if (storedDoctorData) {
            try {
                const data = JSON.parse(storedDoctorData);
                setDoctorInfo(data);
                return;
            } catch (e) {
                console.error('Error parsing stored doctor data:', e);
            }
        }

        if (visit?.doctorId && typeof visit.doctorId === 'object') {
            setDoctorInfo(visit.doctorId);
            return;
        }

        if (user) {
            setDoctorInfo(user);
        }
    }, [doctorData, user, visit]);

    useEffect(() => {
        if (!autoPrint) {
            lastPrintedVisitId.current = null;
            return;
        }

        if (!visit || !patient || !doctorInfo) return;

        if (lastPrintedVisitId.current === visit._id) return;
        lastPrintedVisitId.current = visit._id;

        const handleAfterPrint = () => {
            if (onAfterPrint) onAfterPrint();
        };

        window.addEventListener('afterprint', handleAfterPrint);
        const timer = setTimeout(() => {
            try {
                window.print();
            } catch (error) {
                console.error('Print error:', error);
            }
        }, 300);

        return () => {
            clearTimeout(timer);
            window.removeEventListener('afterprint', handleAfterPrint);
        };
    }, [autoPrint, visit, patient, doctorInfo, onAfterPrint]);

    if (!visit || !patient || !doctorInfo) return null;

    const visitDate = visit.visitDate ? dayjs(visit.visitDate).format('DD/MM/YYYY') : '-';
    const patientAge = patient.dateOfBirth
        ? dayjs().diff(dayjs(patient.dateOfBirth), 'year')
        : patient.age || '-';
    const clinicName = doctorInfo?.clinicName || 'Clinic Name';
    const clinicAddress = doctorInfo?.clinicAddress || 'Clinic Address';
    const clinicPhone = doctorInfo?.clinicPhone || '+1-345 678 9012';

    // Calculate total content items to adjust layout
    const prescriptionCount = visit.prescriptions?.length || 0;
    const labTestCount = visit.labTests?.length || 0;
    const totalItems = prescriptionCount + labTestCount;

    // Determine density class based on content
    let densityClass = 'density-normal';
    if (prescriptionCount > 8 || totalItems > 12) {
        densityClass = 'density-high';
    } else if (prescriptionCount > 5 || totalItems > 8) {
        densityClass = 'density-medium';
    }

    // Only the dosing facts that were actually written are shown: a row of
    // "Dosage: -" chips says nothing and costs a line.
    const doseChips = (prescription) => [
        ['dosage', prescription.dosage],
        ['frequency', prescription.frequency],
        ['duration', prescription.duration]
    ].filter(([, value]) => value && String(value).trim());

    return createPortal(
        <div className="print-prescription" aria-hidden="true">
            <div className={`print-sheet ${densityClass}`}>
                <header className="rx-header">
                    <div className="rx-brand">
                        {/* The clinic's own logo when uploaded, the app emblem otherwise. */}
                        <img src={doctorInfo?.doctorLogo || logo} alt="" className="rx-logo" />
                    </div>
                    <div className="rx-identity">
                        <div className="rx-clinic">{clinicName}</div>
                        {(doctorInfo?.name || doctorInfo?.nameKurdish) && (
                            <div className="rx-doctor">
                                {doctorInfo?.nameKurdish && <span>{doctorInfo.nameKurdish}</span>}
                                {doctorInfo?.nameKurdish && doctorInfo?.name && <span> · </span>}
                                {doctorInfo?.name && <span className="rx-doctor-en">{doctorInfo.name}</span>}
                            </div>
                        )}
                        {doctorInfo?.certificates && (
                            <div className="rx-certificates">{doctorInfo.certificates}</div>
                        )}
                        {doctorInfo?.specialization && (
                            <div className="rx-specialty rx-specialty-en">{doctorInfo.specialization}</div>
                        )}
                        {doctorInfo?.specialization_ku && (
                            <div className="rx-specialty">{doctorInfo.specialization_ku}</div>
                        )}
                        {doctorInfo?.specialization_ar && (
                            <div className="rx-specialty">{doctorInfo.specialization_ar}</div>
                        )}
                        {doctorInfo?.clinicDetail && (
                            <div className="rx-specialty">{doctorInfo.clinicDetail}</div>
                        )}
                    </div>
                </header>

                <section className="rx-patient">
                    <div className="rx-cell">
                        <span className="rx-cell-label">{t('patientName')}</span>
                        <span className="rx-cell-value">{patient.name || '-'}</span>
                    </div>
                    <div className="rx-cell">
                        <span className="rx-cell-label">{t('age')}</span>
                        <span className="rx-cell-value">{patientAge}</span>
                    </div>
                    <div className="rx-cell">
                        <span className="rx-cell-label">{t('gender')}</span>
                        <span className="rx-cell-value">{patient.gender ? t(patient.gender) : '-'}</span>
                    </div>
                    <div className="rx-cell">
                        <span className="rx-cell-label">{t('date')}</span>
                        <span className="rx-cell-value">{visitDate}</span>
                    </div>
                </section>

                {/* The clinic's own watermark when one is uploaded in Settings,
                    otherwise the Rx mark; faint enough to sit behind a full page. */}
                {doctorInfo?.printWatermark ? (
                    <img src={doctorInfo.printWatermark} alt="" className="print-watermark" />
                ) : (
                    <div className="rx-watermark">Rx</div>
                )}

                <section className="rx-body">
                    <div className="rx-symbol">℞</div>
                    {prescriptionCount > 0 ? (
                        <ol className="rx-list">
                            {visit.prescriptions.map((prescription, index) => (
                                <li key={`${prescription.drugName || 'drug'}-${index}`} className="rx-item">
                                    <span className="rx-num">{index + 1}</span>
                                    <div className="rx-item-body">
                                        <div className="rx-drug">{prescription.drugName}</div>
                                        {doseChips(prescription).length > 0 && (
                                            <div className="rx-dose">
                                                {doseChips(prescription).map(([key, value]) => (
                                                    <span key={key} className="rx-chip">
                                                        <span className="rx-chip-label">{t(key)}</span>
                                                        <span>{value}</span>
                                                    </span>
                                                ))}
                                            </div>
                                        )}
                                        {prescription.notes && (
                                            <div className="rx-note">{prescription.notes}</div>
                                        )}
                                    </div>
                                </li>
                            ))}
                        </ol>
                    ) : (
                        <div className="no-data">{t('noPrescriptions')}</div>
                    )}

                    {labTestCount > 0 && (
                        <div className="rx-labs">
                            <div className="rx-labs-title">{t('labTests')}</div>
                            <div className="rx-labs-list">
                                {visit.labTests.map((test, index) => (
                                    <span key={`lab-${index}`} className="rx-lab">
                                        {typeof test === 'string' ? test : (test.testName || '-')}
                                        {typeof test === 'object' && test.result && (
                                            <span className="rx-lab-result">{test.result}</span>
                                        )}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                    {/* The visit's own notes are the doctor's, not the patient's:
                        they stay off the printed prescription. */}
                </section>

                <div className="rx-sign">
                    {doctorInfo?.doctorSignature && (
                        <img src={doctorInfo.doctorSignature} alt="" className="rx-sign-image" />
                    )}
                    <div className="rx-sign-line" />
                    <div className="rx-sign-label">{t('doctorSignature')}</div>
                </div>

                <footer className="rx-footer">
                    <div className="rx-footer-contact">
                        <span className="rx-footer-item">
                            <span className="rx-footer-icon">📍</span>
                            <span>{clinicAddress}</span>
                        </span>
                        <span className="rx-footer-item">
                            <span className="rx-footer-icon">☎</span>
                            <span>{clinicPhone}</span>
                        </span>
                    </div>
                    {doctorInfo?.qrCodeImage && (
                        <img src={doctorInfo.qrCodeImage} alt="" className="rx-footer-qr" />
                    )}
                </footer>
            </div>
        </div>,
        document.body
    );
};

export default PrintPrescription;
